/**
 * QuizCardV2 - Atomic Quiz Card Components (View-Only)
 *
 * @description
 * Reusable atomic components for quiz cards, extracted from EditableQuizCard.
 * These are view-only versions without editing functionality.
 * Components are separated by quiz type (Multiple/OX/Subjective) to match EditableQuizCard structure.
 *
 * @usage
 * ```tsx
 * import { QuizCard } from '@/shared/components/QuizCardV2';
 * import { CheckSmallIcon } from '@zep-us/zus-icons';
 * import { StaticRichTextRenderer } from '@/shared/features/rich-text';
 *
 * // Multiple Choice Quiz (Compound Component Pattern)
 * <QuizCard.Root>
 *   <QuizCard.Header>
 *     <QuizCard.TypeBadge type="multiple" />
 *   </QuizCard.Header>
 *   <QuizCard.Content>
 *     <QuizCard.QuestionText text="<p>What is...</p>" />
 *     <QuizCard.MultipleChoice.Container>
 *       {choices.map((choice, idx) => (
 *         <QuizCard.MultipleChoice.Item key={idx}>
 *           <QuizCard.MultipleChoice.Item.Row>
 *             <QuizCard.MultipleChoice.Item.Indicator.Container
 *               className={isAnswer ? 'bg-green-500' : 'bg-gray-200'}>
 *               {isAnswer && <CheckSmallIcon size="lg" fill="white" />}
 *             </QuizCard.MultipleChoice.Item.Indicator.Container>
 *             <QuizCard.MultipleChoice.Item.Content>
 *               <StaticRichTextRenderer as="div" text={choice.text} className="text-heading-sm-bold" />
 *             </QuizCard.MultipleChoice.Item.Content>
 *           </QuizCard.MultipleChoice.Item.Row>
 *           {choice.imageUrl && (
 *             <QuizCard.MultipleChoice.Item.Image.Container className="size-[62px]">
 *               <QuizCard.MultipleChoice.Item.Image.Thumbnail src={choice.imageUrl} alt="Choice" />
 *             </QuizCard.MultipleChoice.Item.Image.Container>
 *           )}
 *         </QuizCard.MultipleChoice.Item>
 *       ))}
 *     </QuizCard.MultipleChoice.Container>
 *     <QuizCard.Hint type="text" value="Hint text" />
 *   </QuizCard.Content>
 * </QuizCard.Root>
 *
 * // O/X Quiz
 * <QuizCard.Root>
 *   <QuizCard.Content>
 *     <QuizCard.QuestionText text="<p>True or False?</p>" />
 *     <QuizCard.OXChoice.Container answer="o" />
 *   </QuizCard.Content>
 * </QuizCard.Root>
 *
 * // Subjective Quiz (Compound Component Pattern)
 * <QuizCard.Root>
 *   <QuizCard.Content>
 *     <QuizCard.QuestionText text="<p>Explain...</p>" />
 *     <QuizCard.SubjectiveAnswer.Container>
 *       {answers.map((answer, idx) => (
 *         <QuizCard.SubjectiveAnswer.Item key={idx}>
 *           <QuizCard.SubjectiveAnswer.Item.Marker />
 *           <QuizCard.SubjectiveAnswer.Item.Content>
 *             {answer}
 *           </QuizCard.SubjectiveAnswer.Item.Content>
 *         </QuizCard.SubjectiveAnswer.Item>
 *       ))}
 *     </QuizCard.SubjectiveAnswer.Container>
 *   </QuizCard.Content>
 * </QuizCard.Root>
 * ```
 *
 * @module QuizCardV2
 */

export * from './types';

// Import individual components for namespace export
import { QuizCardContent } from './QuizCardContent';
import { QuizCardFooter } from './QuizCardFooter';
import { QuizCardHeader } from './QuizCardHeader';
import { QuizCardRoot } from './QuizCardRoot';
import { QuizHint } from './QuizHint';
import { QuizHintGroup } from './QuizHintGroup';
import { QuizImage } from './QuizImage';
import { QuizMultipleChoice } from './QuizMultipleChoice';
import { QuizOXChoice } from './QuizOXChoice';
import { QuizQuestionText } from './QuizQuestionText';
import { QuizSpacingRule } from './QuizSpacingRule';
import { QuizSubjectiveAnswer } from './QuizSubjectiveAnswer';
import { QuizTypeBadge } from './QuizTypeBadge';
import { TypeBadge } from './TypeBadge';

/**
 * QuizCard - Namespace export with dot notation access
 *
 * @example
 * // Core structure
 * QuizCard.Root
 * QuizCard.Header
 * QuizCard.Content
 * QuizCard.Footer
 *
 * // Question display (with StaticRichTextRenderer for LaTeX support)
 * QuizCard.QuestionText
 *
 * // Image display
 * QuizCard.Image.Container
 * QuizCard.Image.Thumbnail
 * QuizCard.Image.Loading
 *
 * // Quiz type specific components
 * QuizCard.MultipleChoice.Container
 * QuizCard.MultipleChoice.Item (props-based: text, imageUrl, isAnswer, choiceNumber)
 * QuizCard.MultipleChoice.ItemCompound (compound component for layout)
 * QuizCard.MultipleChoice.ItemCompound.Row (indicator + content row)
 * QuizCard.MultipleChoice.ItemCompound.Indicator.Container (indicator container)
 * QuizCard.MultipleChoice.ItemCompound.Content (text content wrapper)
 * QuizCard.MultipleChoice.ItemCompound.Image.Container (image container with ml-[39px] and QuizImage.Container)
 * QuizCard.MultipleChoice.ItemCompound.Image.Thumbnail (image thumbnail, re-exported from QuizImage.Thumbnail)
 * QuizCard.OXChoice.Container
 * QuizCard.SubjectiveAnswer.Container
 * QuizCard.SubjectiveAnswer.Item (props-based: text)
 * QuizCard.SubjectiveAnswer.ItemCompound (compound component for layout)
 * QuizCard.SubjectiveAnswer.ItemCompound.Marker (arrow marker)
 * QuizCard.SubjectiveAnswer.ItemCompound.Content (text content wrapper)
 *
 * // Additional components
 * QuizCard.Hint
 * QuizCard.HintGroup
 * QuizCard.SpacingRule
 * QuizCard.TypeBadge (quiz-specific wrapper)
 * QuizCard.GenericTypeBadge (generic badge for any type)
 */
export const QuizCard = {
  Root: QuizCardRoot,
  Header: QuizCardHeader,
  Content: QuizCardContent,
  Footer: QuizCardFooter,
  QuestionText: QuizQuestionText,
  Image: QuizImage,
  Hint: QuizHint,
  HintGroup: QuizHintGroup,
  SpacingRule: QuizSpacingRule,
  TypeBadge: QuizTypeBadge,
  GenericTypeBadge: TypeBadge,
  MultipleChoice: QuizMultipleChoice,
  OXChoice: QuizOXChoice,
  SubjectiveAnswer: QuizSubjectiveAnswer,
};
